/**
 * Investor View — the Deal Tracker with the spread taken off it.
 *
 * Asked for instead of a separate investor board: one button on the
 * tracker that hides what an investor should not see, so there is one
 * board to keep good rather than two that drift.
 *
 * WHAT THIS IS, said plainly, because the distinction matters:
 *
 * This is a PRESENTATION mode. The browser has already loaded the whole
 * deal set under a staff session, so the numbers are still in the tab —
 * a determined person with developer tools could read them. That makes
 * it exactly right for the thing it is for: turning the laptop round,
 * screen-sharing a call, showing the board to somebody in the room.
 *
 * It is NOT a substitute for a login, and not for a link somebody holds
 * on their own machine. This comment used to say that case was handled
 * by /api/investor-view, "that one never puts a rate or a total into
 * the response at all" — there is no such route. It was never built, or
 * it went and the note stayed. Either way a reader who needs that case
 * and trusts this paragraph reaches for the toggle instead, which is
 * the one thing the rest of this comment says not to do.
 *
 * So, plainly: the case is unhandled. Giving an investor their own view
 * needs a route that filters server-side, because anything this file
 * does happens after the numbers are already in the tab.
 *
 * HOW IT HIDES, which is the part worth getting right:
 *
 * Not by wrapping ten render sites in `investorView && …`. The tracker
 * prints the margin on the deal card, on the Funded card, in the mobile
 * metric row and in the KPI strip, and a list like that is one addition
 * away from leaking — somebody adds an eleventh and nobody remembers.
 *
 * So the FIELDS GO. investorSafeDeal drops them from the deal objects
 * before anything renders, and every consumer then draws nothing of its
 * own accord: `hasMargin` is false, the Buy metric reads '-', the KPI
 * sums to zero. A surface added tomorrow that reads deal.margin gets
 * undefined without anybody having thought about it.
 *
 * What stays is the SELL side: market value, the stage's market total,
 * acreage, counties, stage and dates. Margin is sell minus buy, so
 * withholding the buy side alone is arithmetically enough — and the
 * position's worth is the thing an investor is actually being shown.
 */

// Buy-side and spread figures, on the enriched deal the tracker builds
// (see the memo in DealsDashboard that returns buyTotal/sellTotal/margin)
// and on the raw row underneath it.
export const INVESTOR_HIDDEN_DEAL_FIELDS = Object.freeze([
  'margin',
  'buyTotal',
  'buyRateNRA',
  'buyRateNMA',
  'offerAmount',
  'offerTotal',
]);

const STORAGE_KEY = 'dealsDashboard.investorView';

/**
 * Whether the app is in Investor View.
 *
 * Remembered across reloads deliberately. Opening a deal and coming back
 * is the normal thing to do mid-demonstration, and a mode that fell off
 * on every navigation would put the margins back on screen in front of
 * the person it was turned on for.
 */
export function readInvestorView(storage) {
  try {
    const store = storage || (typeof window === 'undefined' ? null : window.localStorage);
    return store?.getItem(STORAGE_KEY) === '1';
  } catch {
    // A blocked or full localStorage must not take the app down, and
    // OFF is the safe answer: staff see their own numbers.
    return false;
  }
}

export function writeInvestorView(on, storage) {
  try {
    const store = storage || (typeof window === 'undefined' ? null : window.localStorage);
    if (on) store?.setItem(STORAGE_KEY, '1');
    else store?.removeItem(STORAGE_KEY);
  } catch {
    // Not being able to remember it is not a reason to refuse the click.
  }
}

/**
 * One switch, read from anywhere.
 *
 * The button is in the app header and the things it hides are on the
 * Deal Tracker and inside the Owner Dashboard's interest estimator —
 * three components with no parent between them but the router. A store
 * rather than a context because the value is a single boolean that
 * changes on a click: a context provider would re-render the whole tree
 * on every unrelated render of its parent, and this is a page that
 * already redraws a hundred cards.
 *
 * useSyncExternalStore wants a stable snapshot, so the boolean is held
 * here and only recomputed when it is set. Reading localStorage in
 * getSnapshot would be a fresh read on every render — and on a blocked
 * store, a fresh throw.
 */
const investorViewStore = (() => {
  const listeners = new Set();
  let current = readInvestorView();
  return {
    subscribe(listener) {
      listeners.add(listener);
      return () => listeners.delete(listener);
    },
    getSnapshot: () => current,
    // Server rendering has no localStorage and no demonstration in
    // progress; OFF is the only honest answer.
    getServerSnapshot: () => false,
    set(on) {
      const next = !!on;
      if (next === current) return;
      current = next;
      writeInvestorView(next);
      for (const listener of listeners) listener();
    },
    toggle() {
      investorViewStore.set(!current);
    },
  };
})();

export const investorView = investorViewStore;

/** One deal, with the buy side and the spread removed. */
export function investorSafeDeal(deal) {
  if (!deal || typeof deal !== 'object') return deal;
  const safe = { ...deal };
  for (const field of INVESTOR_HIDDEN_DEAL_FIELDS) delete safe[field];
  // The estimator rows carry their own rates, and the card's buyer /
  // investor labels are built from them. Rows are not rendered on the
  // tracker, but they ride along on the deal and anything that starts
  // reading them would be reading buy rates.
  if (Array.isArray(safe.interestEstimates)) {
    safe.interestEstimates = safe.interestEstimates.map((row) => {
      if (!row || typeof row !== 'object') return row;
      const { buyRate, buyTotal, ...rest } = row;
      return rest;
    });
  }
  // The likelihood rating is the agent's own read on whether a deal
  // lands — an internal sales judgement, written for the desk. It is
  // not a buy price, but it is the same KIND of thing: something said
  // candidly because the room was ours. A card telling an investor we
  // think their deal is a long shot is not a decision to make by
  // forgetting to strip a field.
  if (safe.lifecycle && typeof safe.lifecycle === 'object' && 'likelihood' in safe.lifecycle) {
    const { likelihood, ...lifecycle } = safe.lifecycle;
    void likelihood;
    safe.lifecycle = lifecycle;
  }
  return safe;
}

/** The whole board. */
export function investorSafeDeals(deals) {
  return (Array.isArray(deals) ? deals : []).map(investorSafeDeal);
}
